"use client";

import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import type { AccordionSectionComponent } from "../../types";
import { isParagraphHtml } from "../../utils/paragraphHtml";
import { paragraphHtmlToSafeHtml } from "../../utils/inlineIcons";

const BG_COLOR = "#EDF7FC";
const TITLE_COLOR = "#005CA9";
const ICON_COLOR = "#FFA401";

/** A paragraph can be plain text or a rich-text HTML string (editorial content
 *  authored in the admin's rich editor). HTML goes through the shared
 *  sanitizer before being injected — never render payload HTML raw. */
function renderParagraph(p: string, i: number) {
  if (isParagraphHtml(p)) {
    return (
      <p
        key={i}
        className="rich-paragraph-block"
        dangerouslySetInnerHTML={{ __html: paragraphHtmlToSafeHtml(p) }}
      />
    );
  }
  return <p key={i}>{p}</p>;
}

/** Accordion section renderer. Single-open behaviour: expanding a panel
 *  collapses the previous one (openIndex holds at most one index). */
export function AccordionSectionRenderer({ component }: { component: AccordionSectionComponent }) {
  const { items } = component.content;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!items?.length) return null;

  return (
    <section className="space-y-4" aria-label="Accordion">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const paragraphs = item.paragraphs ?? [];

        return (
          <div
            key={index}
            className="overflow-hidden rounded-xl"
            style={{ backgroundColor: BG_COLOR }}
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:opacity-90"
              aria-expanded={isOpen}
              aria-controls={`accordion-body-${index}`}
              id={`accordion-heading-${index}`}
            >
              <span
                className="text-base xl:text-lg font-semibold md:text-lg"
                style={{ color: TITLE_COLOR }}
              >
                {item.title || `Item ${index + 1}`}
              </span>
              <span className="shrink-0" style={{ color: ICON_COLOR }} aria-hidden>
                {isOpen ? (
                  <Minus className="h-5 w-5" />
                ) : (
                  <Plus className="h-5 w-5" />
                )}
              </span>
            </button>
            <div
              id={`accordion-body-${index}`}
              role="region"
              aria-labelledby={`accordion-heading-${index}`}
              hidden={!isOpen}
              className="border-t border-[#005CA9]/20"
            >
              {isOpen && paragraphs.length > 0 && (
                <div
                  className="space-y-3 px-4 py-4 text-base xl:text-lg text-[#0F2849] rich-paragraph-block"
                  style={{ backgroundColor: "rgba(255,255,255,0.5)" }}
                >
                  {paragraphs.map(renderParagraph)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
